<script setup>
import Layout from "../Layouts/Customer.vue";
import RadioButton from "primevue/radiobutton";
import {useForm} from "@inertiajs/inertia-vue3";
import Button from "primevue/button";
import {computed, ref} from "vue";
import {required$, true$} from "../utils/validators";
import {useVuelidate} from "@vuelidate/core";
import {scrollToFirstError, yesNoOptions} from "../utils/form";
import {dataURLtoFile} from "../utils/file-utils";
import {useToast} from "primevue/usetoast";
import moment from 'moment';
import ConsentSignature from "../Components/ConsentSignature.vue";

const props = defineProps([
    'customer',
    'menuItems',
    'flash',
]);

const subscriptionTypes = [
    {
        label: 'Fitness-Abo ohne Zugang zur Ferninfrarot-Sauna',
        value: false,
    },
    {
        label: 'Fitness-Abo mit Zugang zur Ferninfrarot-Sauna',
        value: true
    },
]

const subscriptionDurations = [
    {
        label: 'Fitness-Abo mit 3-monatiger Laufzeit',
        value: 3,
    },
    {
        label: 'Fitness-Abo mit 6-monatiger Laufzeit',
        value: 6
    },
    {
        label: 'Fitness-Abo mit 12-monatiger Laufzeit',
        value: 12
    },
]

const toast = useToast()
const consent = ref(props.customer?.infrarot_sauna_consent)
const consentSlug = ref('infrarot-sauna-consents')
const isEdit = ref(Boolean(consent.value))
const submitting = ref(false)
const consentSignature = ref(null)
const date = ref(
    isEdit.value ? moment(consent.value.updated_at) : moment()
)
const form = useForm({
    is_agree_documentation: isEdit.value ? consent.value.is_agree_documentation : null,
    signature_key: null,
    customer_uuid: props.customer.uuid,
    is_agree_signature: isEdit.value,
    signature_img: isEdit.value ? consent.value.signature_img : null,
    has_signature: null,
    date: date.value.format('DD.MM.YYYY'),
})

const subscriptionType = computed(() => {
    let value = false

    props.customer.customer_unlimited_plan.unlimited_plan.devices.some((data) => {
        if (data.name == 'TYLÖ IR-DETOX Infrarotsauna') {
            value = true

            return true
        }

        return false
    })

    return value
})

const subscriptionDuration = computed(() => {
    return props.customer.customer_unlimited_plan.unlimited_plan.months
})

const rules = {
    form: {
        is_agree_documentation: {
            required$,
        },
        is_agree_signature: {
            true$,
        },
        signature_key: {
            custom$: {
                $validator: function(value) {
                    return form.has_signature !== false
                },
                $message: 'Dieses Feld ist erforderlich',
            }
        },
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const submit = async (e, isDownload) => {
    let signatureData = null
    const signaturePad = consentSignature.value.getSignaturePad()

    if (signaturePad) {
        const { isEmpty, data } = signaturePad.saveSignature()

        form.has_signature = ! isEmpty
        signatureData = data
    }

    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()
        toast.add({
            severity: "error",
            summary: "Error",
            life: 5000,
        });

        return
    }

    submitting.value = true

    if (signatureData) {
        const { key:signatureKey } = await Vapor.store(dataURLtoFile(signatureData), {
            progress: progress => {
            }
        })

        form.signature_key = signatureKey
    }

    const postUrl = isEdit.value
        ? `/${consentSlug.value}/${consent.value.uuid}`
        : `/${consentSlug.value}`

    form.post(
        postUrl,
        {
            preserveState: false,
            onError: (errors) => {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    life: 5000,
                });
            },

            onSuccess: (page) => {
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000,
                });

                if (isDownload) {
                    window.location.href = `/${consentSlug.value}/${consent.value.uuid}/download`
                }
            },

            onFinish: (visit) => {
                submitting.value = false;
            }
        }
    )
}

const download = async () => {
    if (! submitting.value) {
        await submit(null, true)
    }
}
</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="px-14 py-7">
            <div class="relative mb-9">
                <h1 class="text-black text-base font-bold">
                    Einverständniserklärung und Vertrag Vigory Fitness mit/ohne Ferninfrarot-Sauna
                </h1>

                <div v-if="isEdit"
                     class="absolute top-0 right-0">
                    <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer" @click="download"></i>
                </div>
            </div>

            <div class="text-sm">
                <h3 class="text-black font-bold">Einführung</h3>
                <div class="grid grid-cols-1 gap-y-3 mb-9">
                    <p>Infrarot- und Vakuum-Trainingsgeräte kombinieren die Vorteile von Infrarotstrahlung und
                        Unterdrucktechnologie, um den Trainingseffekt zu maximieren. Diese Geräte werden zur Verbesserung
                        der Durchblutung, für den Aufbau der Muskulatur, für die Reduktion von Cellulite und die Unterstützung
                        der Gewichtsabnahme eingesetzt.</p>
                    <p>Ferninfrarot-Sauna</p>
                    <p>Die IR-Detox-Sauna nutzt Ferninfrarotstrahlung. Diese Strahlung dringt tief in die Haut ein,
                        fördert die Durchblutung und Entgiftung und hilft bei der Entspannung und Schmerzlinderung.</p>
                </div>

                <ul class="mb-9">
                    <li>
                        <h3 class="text-sm font-bold my-5">1. Vertragsgegenstand</h3>
                        <p>Dieser Vertrag regelt das Rechtsverhältnis zwischen der Betreiberin des VIGORY Center, Sani Medical AG
                            („VIGORY“), und Ihnen als Kundin bzw. Kunde im Zusammenhang mit dem VIGORY®-Fitness-Abo.
                        </p>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">2. Abonnemente und Gebühren</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>VIGORY bietet folgende Abonnemente und Leistungen an</p>

                            <ul class="list-disc ml-5 grid grid-cols-1 gap-y-1">
                                <li>Fitness-Abo zur nicht-exklusiven Benutzung der von VIGORY bereitgestellten Infrarot- und
                                    Vakuum-Trainingsgeräten während den regulären Öffnungszeiten („Fitness-Abo“);</li>

                                <li>Fitness-Abo (vgl. oben) inklusive Zugang zu den Ferninfrarot-Saunen.</li>
                            </ul>

                            <p>Beide Fitness-Abonnemente können entweder eine Laufzeit von 3, 6 oder 12 Monate haben.</p>

                            <p>Der konkrete Umfang und die jeweiligen Gebühren der Abonnemente sind auf unserer Website
                                ersichtlich und können unter folgendem Link abgerufen werden. https://www.vigory.ch/fitness</p>

                            <p>Es gelten die im Zeitpunkt des Vertragsabschlusses ausgewiesenen Gebühren. VIGORY behält sich das
                                Recht vor, das Angebot (Standort, Trainingszeiten, Trainingsgeräte etc.) jederzeit zu ändern.</p>

                            <p>Bitte geben Sie an, welches Abonnement und welche Laufzeit Sie abschliessen möchten: </p>

                            <div>
                                <div class="grid grid-cols-2 gap-4">
                                    <div class="flex flex-col gap-y-2">
                                        <div
                                            v-for="(data, i) in subscriptionTypes"
                                            :key="`subscription_type_${i}`"
                                        >
                                            <div class="flex items-center gap-4">
                                                <RadioButton
                                                    readonly
                                                    v-model="subscriptionType"
                                                    :value="data.value"
                                                />

                                                <div class="">{{ data.label }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex flex-col gap-y-2">
                                        <div
                                            v-for="(data, i) in subscriptionDurations"
                                            :key="`subscription_type_${i}`"
                                        >
                                            <div class="flex items-center gap-4">
                                                <RadioButton
                                                    readonly
                                                    v-model="subscriptionDuration"
                                                    :value="data.value"
                                                />

                                                <div class="">{{ data.label }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">3. Mitgliedschaft</h3>

                        <ul class="grid grid-cols-1 gap-y-3">
                            <li>
                                a. Mindestalter: Die Mitgliedschaft bei VIGORY Fitness ist Personen vorbehalten, die das 18. Lebensjahr
                                vollendet haben. Personen unter 18 Jahren können die Leistungen in Ausnahmefällen und nur mit
                                schriftlicher Zustimmung eines Erziehungsberechtigten in Anspruch nehmen.
                            </li>

                            <li>
                                b. Gesundheitlicher Zustand: Vor Abschluss dieses Vertrags führt VIGORY eine Anamnese
                                (Gesundheitsfragebogen, der wahrheitsgetreu ausgefüllt werden muss) durch. Je nach Resultat der
                                Anamnese kann Ihnen VIGORY eine vorgängige ärztliche Konsultation und/oder den Verzicht auf
                                bestimmte Trainingsgeräte empfehlen. Die Anamnese stellt keine medizinische Beratung dar.
                            </li>

                            <li>
                                c. Nicht-Übertragbarkeit: Die Mitgliedschaft ist persönlich und nicht übertragbar. Es ist Ihnen nicht
                                gestattet, Ihre Zugangsberechtigung Dritten zur Verfügung zu stellen. Bei Verstössen gegen diese
                                Bestimmung ist VIGORY zur sofortigen Kündigung des Vertrags berechtigt, ohne dass Ihnen ein Anspruch
                                auf Rückzahlung des bezahlten Mitgliedschaftsbeitrags zusteht.
                            </li>

                            <li>
                                d. Eintrittskontrolle: Der Zugang zu den Räumlichkeiten von VIGORY Fitness erfolgt über eine App. Sie
                                erhalten individuelle Zugangsdaten, die für den Einlass erforderlich sind. Anhand der App-Aufzeichnungen
                                kann VIGORY nachvollziehen, wann Sie sich in den Räumlichkeiten aufhalten. Diese Daten werden zu
                                Sicherheitszwecken erhoben und nur in Notfällen ausgewertet.
                            </li>

                            <li>
                                <div class="grid grid-cols-1 gap-y-1">
                                    <p>e. Fernbetreute Trainingszeit: Der Zugang zum Trainingsbereich des Fitnesscenters ist für Kunden mit
                                        einer gültigen Mitgliedschaft auch ausserhalb der betreuten Trainingszeiten möglich. Im Empfangsbereich
                                        befinden sich die Notfallausrüstung, einschliesslich Notfalltelefon mit gespeicherten Notrufnummern,
                                        Notfallkoffer und Defibrillator, um im Notfall den Rettungsdienst zu alarmieren und die notwendigen  Massnahmen einzuleiten.</p>
                                    <p>
                                        Beim Abschluss einer Mitgliedschaft oder während der persönlichen Einweisung in die Geräte werden Sie direkt und umfassend über das Verhalten im Notfall sowie die Rettungskette informiert.
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">4. Mögliche Nebenwirkungen und Risiken</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>Wie bei jeder körperlichen Aktivität und Anwendung von Infrarot bestehen potenzielle Risiken und
                                Nebenwirkungen, die auftreten können, z.B. Muskelkater oder -schmerzen, Überhitzung oder
                                Dehydratation, Schwindel oder Benommenheit, Hautreizungen.</p>
                            <p>
                                Es wird allgemein empfohlen, dass Sie ab einem Alter von über 45 Jahren (Männer) bzw. 55 Jahren
                                (Frauen) vor Beginn des Trainings eine ärztliche Untersuchung durchführen lassen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">5. Zahlung</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>Die Abo-Gebühr richtet sich nach der im Zeitpunkt des Vertragsabschlusses geltenden Gebührenliste, die
                                unter folgendem Link abgerufen werden kann: https://www.vigory.ch/fitness
                            </p>

                            <p>
                                Sie erhalten die Zugangsdaten erst nachdem die vollständige Zahlung bei VIGORY eingegangen ist.
                            </p>

                            <p>Solange die Zahlung der Abo-Gebühr nicht geleistet wurde, besteht kein Zutrittsrecht. Die Zahlung ist
                                unabhängig davon geschuldet, ob das Angebot tatsächlich genutzt wird.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">6. Haftung</h3>

                        <ul class="grid grid-cols-1 gap-2">
                            <li>
                                6.1 Die Benutzung des Angebots von VIGORY, einschliesslich der Trainingsgeräte und gegebenenfalls
                                der Saunabereiche, erfolgt auf Ihre eigene Verantwortung und Ihr eigenes Risiko. VIGORY schliesst,
                                soweit gesetzlich zulässig, jede Haftung für direkte und indirekte Schäden aus. Es liegt in Ihrer
                                Verantwortung, eine Versicherung abzuschliessen.
                            </li>

                            <li>
                                6.2 VIGORY haftet nicht für Schäden, die auf Umstände zurückzuführen sind, für die Sie
                                verantwortlich sind. Sie haften gegenüber VIGORY für Schäden, die Sie an Geräten und an der
                                Anlage von VIGORY verursachen. Reparatur- und/oder Verlustkosten sind VIGORY vollumfänglich zu
                                erstatten.
                            </li>

                            <li>
                                6.3 VIGORY übernimmt keine Haftung für Verlust, Diebstahl oder Beschädigung Ihrer persönlichen
                                Gegenstände, die in den Räumlichkeiten von VIGORY aufbewahrt oder genutzt werden. Es wird
                                empfohlen, keine wertvollen Gegenstände mitzubringen oder unbeaufsichtigt zu lassen.
                            </li>

                            <li>
                                6.4 Die von VIGORY vor dem Vertragsabschluss durchgeführte Anamnese stellt keine medizinische
                                Beratung dar noch ersetzt sie eine solche. Es liegt in Ihrer eigenen Verantwortung, bei allfälligen
                                medizinischen Bedenken eine ärztliche Untersuchung durchführen zu lassen.
                            </li>

                            <li>
                                6.5 VIGORY garantiert keine spezifischen Resultate und keinen spezifischen Behandlungserfolg.
                            </li>
                        </ul>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">7. Datenschutz</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>VIGORY erhebt und bearbeitet Personendaten des Kunden nach Massgabe der Datenschutzerklärung,
                                die unter folgendem Link abgerufen werden kann: https://www.vigory.ch/datenschutz</p>

                            <p>Der Kunde stimmt der fotografischen Dokumentation der behandelten Bereiche und der anonymen
                                Veröffentlichung der Ergebnisse zu Werbezwecken zu.
                            </p>

                            <div>
                                <div class="flex gap-5">
                                    <div
                                        v-for="(data, i) in yesNoOptions"
                                        :key="`is_agree_documentation_${i}`"
                                    >
                                        <div class="flex items-center gap-4">
                                            <RadioButton
                                                :invalid="v$.form.is_agree_documentation.$error"
                                                v-model="form.is_agree_documentation"
                                                :value="data.value"
                                                @change="v$.form.is_agree_documentation.$touch"
                                            />

                                            <div class="">{{ data.label }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="v$.form.is_agree_documentation.$error" class="text-sm text-[#dc2626]">
                                    {{ v$.form.is_agree_documentation.$errors[0].$message }}
                                </div>
                            </div>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">8. Einwilligung zur Videoüberwachung</h3>

                        <p>Mit Ihrer Unterschrift erklären Sie sich damit einverstanden, dass in den Räumlichkeiten von VIGORY,
                            insbesondere im Eingangs- und Trainingsbereich, eine Videoüberwachung erfolgt. Die Überwachung
                            erfolgt ausschliesslich zur Gewährleistung der Qualitop-Zertifizierung sowie der Sicherheit (Personen
                            und Sachen). Die Aufzeichnungen werden vertraulich behandelt, nach angemessener Frist gelöscht
                            und ausschliesslich im Falle von sicherheitsrelevanten Vorfällen ausgewertet. </p>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">9. Hausordnung</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>Die aktuelle Version der Hausordnung kann über den folgenden Link abgerufen werden: [Link].</p>

                            <div>Mit Ihrer Unterschrift akzeptieren Sie die zum Zeitpunkt des Vertragsabschlusses geltende Version der
                                Hausordnung und verpflichten sich, diese jederzeit einzuhalten und allfälligen Anweisungen des
                                Personals von VIGORY Folge zu leisten. Bei einem groben oder wiederholten Verstoss gegen die
                                Hausordnung und/oder Anweisungen des Personals ist VIGORY berechtigt, ein Hausverbot
                                auszusprechen, diesen Vertrag mit sofortiger Wirkung aufzulösen und den Zugang via App abzuschalten.
                                Sie haben in diesem Fall keinen Anspruch auf eine Rückerstattung der Abo-Gebühr. Bei
                                schwerwiegenden Verstössen gegen die Hausordnung ist eine vorgängige Abmahnung nicht erforderlich.</div>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">10. Änderungsvorbehalt</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>VIGORY behält sich das Recht vor, die vertraglichen Bestimmungen jederzeit zu ändern oder anzupassen.
                                Allfällige Änderungen werden Ihnen per App mitgeteilt und treten in Kraft, sofern Sie der Änderung nicht
                                innert 30 Kalendertagen ab Mitteilung schriftlich widersprechen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">11. Vertragsdauer und Kündigungsmöglichkeit</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>Die Vertragsdauer ergibt sich aus diesem Vertrag und beginnt mit der Vertragsunterzeichnung. Der
                                Vertrag endet automatisch nach Ablauf der vereinbarten Vertragsdauer. VIGORY steht es frei, einem
                                Rücktritt vom Vertrag mit anteilsmässiger Rückerstattung der Abo-Gebühr aus wichtigen Gründen
                                zuzustimmen (z.B. bei andauernder Krankheit oder Unfall sowie bei einem Wohnsitzwechsel ins Ausland).</p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">12. Anwendbares Recht und Gerichtsstand</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            Diese Vereinbarung untersteht materiellem schweizerischem Recht, unter Ausschluss des Wiener
                            Übereinkommens der Vereinten Nationen über Verträge über den internationalen Warenkauf. Für
                            Streitigkeiten aus diesem Vertrag sind ausschliesslich die Gerichte am Sitz von VIGORY zuständig.
                            Vorbehalten bleiben zwingende gesetzliche Gerichtsstände.
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">13. Schlussbestimmungen</h3>

                        <div class="grid grid-cols-1 gap-y-3">
                            <p>Die Angaben im Anamnesefragebogen habe ich wahrheitsgemäss beantwortet.</p>

                            <p>Wir bitten Sie, uns umgehend über jegliche Änderungen Ihres Gesundheitszustandes zu informieren.</p>
                        </div>
                    </li>
                </ul>

                <div class="mb-9">
                    <ConsentSignature
                        ref="consentSignature"
                        :customer="customer"
                        :form="form"
                        :v$="v$"
                        :is-edit="isEdit"
                    />
                </div>

                <div class="flex justify-end">
                    <Button
                        @click="submit"
                        :loading="submitting"
                        label="speichern"
                        class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                    />
                </div>
            </div>
        </div>
    </Layout>
</template>
